/* global tinymce */
import { buildDateSpan, computeDatePreview, readSpanData } from "../../visualization/dynamicDate";
import { CAPITALIZATION_VALUES } from "../../constants";

const PREDEFINED_TAB = "Predefined Date Formats";
const CUSTOM_TAB = "Custom Date Formats";

const languageItems = [
  {
    text: "English",
    value: "en",
  },
  {
    text: "Spanish (Español)",
    value: "es",
  },
];

const caseItems = [
  {
    text: "Default (e.g. Monday / Lunes)",
    value: CAPITALIZATION_VALUES.DEFAULT,
  },
  {
    text: "lowercase (e.g. monday / lunes)",
    value: CAPITALIZATION_VALUES.LOWER,
  },
  {
    text: "UPPERCASE (e.g. MONDAY / LUNES)",
    value: CAPITALIZATION_VALUES.UPPER,
  },
];

const patternFromForm = (data, currentTab) =>
  currentTab === CUSTOM_TAB ? data.customDateFormat : data.dateType;

const previewFromForm = (data, currentTab) =>
  computeDatePreview(patternFromForm(data, currentTab), data.dateOffset, data.dateLocale, data.dateCase);

const spanFromFormData = (doc, data) =>
  buildDateSpan(
    doc,
    patternFromForm(data, activeTab),
    data.dateOffset || "0",
    data.dateLocale || "en",
    data.dateCase || "default",
  );

const DEFAULT_FORMAT = "EEEE, MMMM d, yyyy";

const localeField = { type: "listbox", name: "dateLocale", label: "Language", items: languageItems };
const caseField = { type: "listbox", name: "dateCase", label: "Capitalization", items: caseItems };
const offsetField = { type: "input", name: "dateOffset", label: "Date Offset in days", placeholder: "0" };
const previewField = {
  type: "input",
  name: "preview",
  label: "Preview (today's date with current settings)",
  disabled: true,
};

const DEFAULT_INITIAL = {
  dateLocale: "en",
  dateCase: "default",
  dateType: DEFAULT_FORMAT,
  dateOffset: "",
  customDateFormat: "",
};

let activeTab = PREDEFINED_TAB;
const dynamicDateConfig = {
  title: "Dynamic Date",
  body: {
    type: "tabpanel",
    tabs: [
      {
        name: PREDEFINED_TAB,
        title: "Predefined",
        items: [
          localeField,
          caseField,
          {
            type: "listbox",
            name: "dateType",
            label: "Select Predefined Date Format (previewed in English)",
            disabled: false,
            items: [
              // Full date with weekday
              {
                text: "Monday, January 31, 2000  (EEEE, MMMM d, yyyy)",
                value: "EEEE, MMMM d, yyyy",
              },
              {
                text: "Monday, 31 January, 2000  (EEEE, d MMMM, yyyy)",
                value: "EEEE, d MMMM, yyyy",
              },
              {
                text: "Mon, Jan 31, 2000  (EEE, MMM d, yyyy)",
                value: "EEE, MMM d, yyyy",
              },
              // Full date without weekday
              {
                text: "January 31, 2000  (MMMM d, yyyy)",
                value: "MMMM d, yyyy",
              },
              {
                text: "31 January, 2000  (d MMMM, yyyy)",
                value: "d MMMM, yyyy",
              },
              {
                text: "Jan 31, 2000  (MMM d, yyyy)",
                value: "MMM d, yyyy",
              },
              {
                text: "31 Jan, 2000  (d MMM, yyyy)",
                value: "d MMM, yyyy",
              },
              // Numeric / machine-readable
              {
                text: "01/31/2000  (MM/dd/yyyy)",
                value: "MM/dd/yyyy",
              },
              {
                text: "31/01/2000  (dd/MM/yyyy)",
                value: "dd/MM/yyyy",
              },
              {
                text: "1/31/2000  (M/d/yyyy)",
                value: "M/d/yyyy",
              },
              {
                text: "2000-01-31  (yyyy-MM-dd)",
                value: "yyyy-MM-dd",
              },
              {
                text: "31.01.2000  (dd.MM.yyyy)",
                value: "dd.MM.yyyy",
              },
              {
                text: "31 01 2000  (d MM yyyy)",
                value: "d MM yyyy",
              },
              {
                text: "2000 01 31  (yyyy MM d)",
                value: "yyyy MM d",
              },
              // Month + day
              {
                text: "January 31  (MMMM d)",
                value: "MMMM d",
              },
              {
                text: "Jan 31  (MMM d)",
                value: "MMM d",
              },
              {
                text: "Mon, Jan 31  (EEE, MMM d)",
                value: "EEE, MMM d",
              },
              {
                text: "01 31  (MM d)",
                value: "MM d",
              },
              {
                text: "31 01  (d MM)",
                value: "d MM",
              },
              // Month + year
              {
                text: "January 2000  (MMMM yyyy)",
                value: "MMMM yyyy",
              },
              {
                text: "Jan 2000  (MMM yyyy)",
                value: "MMM yyyy",
              },
              // Single components
              {
                text: "Monday  (EEEE)",
                value: "EEEE",
              },
              {
                text: "Mon  (EEE)",
                value: "EEE",
              },
              {
                text: "January  (MMMM)",
                value: "MMMM",
              },
              {
                text: "Jan  (MMM)",
                value: "MMM",
              },
              {
                text: "2000  (yyyy)",
                value: "yyyy",
              },
            ],
          },
          offsetField,
          previewField,
        ],
      },
      {
        name: CUSTOM_TAB,
        title: "Custom",
        items: [
          {
            type: "htmlpanel",
            html: '<span>To use a custom date format, please <a target="_blank" href="https://help.siterunner.com/help/design-elements-what-is-a-dynamic-date-button?version=latest">see documentation</a>.</span>',
          },
          localeField,
          caseField,
          {
            type: "input",
            name: "customDateFormat",
            inputMode: "text",
            placeholder: "dddd, MMMM d, yyyy",
            disabled: false,
          },
          offsetField,
          previewField,
        ],
      },
    ],
  },
  initialData: {
    ...DEFAULT_INITIAL,
    preview: previewFromForm(DEFAULT_INITIAL, PREDEFINED_TAB),
  },
  buttons: [
    {
      type: "cancel",
      name: "closeButton",
      text: "Cancel",
    },
    {
      type: "submit",
      name: "submitButton",
      text: "Insert",
      primary: true,
    },
  ],
  onTabChange: (api, details) => {
    activeTab = details.newTabName || activeTab;
    api.setData({ preview: previewFromForm(api.getData(), activeTab) });
  },
  onChange: (api, details) => {
    if (details.name === "preview") return;
    const data = api.getData();
    const nextPreview = previewFromForm(data, activeTab);
    if (data.preview !== nextPreview) api.setData({ preview: nextPreview });
  },
  onSubmit: function (api) {
    const span = spanFromFormData(document, api.getData());
    tinymce.activeEditor.execCommand("mceInsertContent", false, span.outerHTML);
    activeTab = PREDEFINED_TAB;
    api.close();
  },
  onCancel: () => {
    activeTab = PREDEFINED_TAB;
  },
};

const PREDEFINED_FORMATS = dynamicDateConfig.body.tabs
  .find(t => t.name === PREDEFINED_TAB)
  .items.find(i => i.name === "dateType")
  .items.map(i => i.value);

const editDynamicDate = (editor, element) => {
  const { fmt, offset, locale, variant } = readSpanData(element, DEFAULT_FORMAT);
  const isPredefined = PREDEFINED_FORMATS.includes(fmt);
  activeTab = isPredefined ? PREDEFINED_TAB : CUSTOM_TAB;

  const form = {
    dateLocale: locale,
    dateCase: variant,
    dateType: isPredefined ? fmt : DEFAULT_FORMAT,
    customDateFormat: isPredefined ? "" : fmt,
    dateOffset: offset,
  };
  const initialData = { ...form, preview: previewFromForm(form, activeTab) };

  const dialogApi = editor.windowManager.open({
    ...dynamicDateConfig,
    initialData,
    onSubmit: function (api) {
      const newSpan = spanFromFormData(element.ownerDocument || document, api.getData());
      editor.selection.select(element);
      editor.insertContent(newSpan.outerHTML);
      activeTab = PREDEFINED_TAB;
      api.close();
    },
  });

  // Focusing the custom field is the only reliable way to switch tabs visually after open
  if (!isPredefined && dialogApi?.focus) dialogApi.focus("customDateFormat");
};

export { dynamicDateConfig, editDynamicDate };
